import {
  mapOpportunitySkillsForCard,
  resolveOpportunityOrganisationLogoUrl,
  showsCandidateSavedBookmark,
  resolveListingProviderFromOpportunity,
  candidateJobPromotionBadge,
} from '@yeshuman/sdk'
import { memo } from 'react'

import { JobCard, type JobCardProps } from '../job-card'
import { JobApplicationTrailing } from './job-application-trailing'
import type { Opportunity } from '../../hooks/use-opportunities'
import type { ApplicationStatusEntry } from '../../hooks/use-my-applications'
import { useJobsBoardTenantLogoSrc } from '../../hooks/use-jobs-board-tenant-logo-src'
import { toOpportunityJobSummaryInput } from '../../utils/opportunity-job-meta'

const EMPTY_MATCHED_SKILL_KEYS: string[] = []

export type CandidateOpportunityJobCardFitProps = Pick<
  JobCardProps,
  'matchBand' | 'matchBandCaption' | 'fitInsight'
>

export type CandidateOpportunityJobCardProps = {
  opportunity: Opportunity
  application?: Pick<ApplicationStatusEntry, 'status'> | null
  saved?: boolean
  isSaving?: boolean
  matchedSkillKeys?: string[]
  variant?: JobCardProps['variant']
  className?: string
  /** `always` — saved-job rows always show save; `bookmark` — browse rules. */
  saveMode?: 'bookmark' | 'always'
  fitProps?: CandidateOpportunityJobCardFitProps
  onOpenJob: (opportunity: Opportunity) => void
  onSaveToggle?: (opportunityId: string) => void
  onApply: (opportunity: Opportunity) => void
}

function candidatePromotionBadge(opportunity: Opportunity) {
  if (opportunity.kind === 'marketplace') return null
  return candidateJobPromotionBadge({
    isFeatured: opportunity.is_featured,
    publishedAt: opportunity.published_at,
  })
}

export const CandidateOpportunityJobCard = memo(function CandidateOpportunityJobCard({
  opportunity,
  application,
  saved = false,
  isSaving = false,
  matchedSkillKeys = EMPTY_MATCHED_SKILL_KEYS,
  variant = 'standard',
  className,
  saveMode = 'bookmark',
  fitProps,
  onOpenJob,
  onSaveToggle,
  onApply,
}: CandidateOpportunityJobCardProps) {
  const tenantLogoFallbackSrc = useJobsBoardTenantLogoSrc()
  const showBookmark = showsCandidateSavedBookmark(application)
  const canSave = onSaveToggle != null && (saveMode === 'always' || showBookmark)

  return (
    <JobCard
      className={className}
      variant={variant}
      onOpen={() => onOpenJob(opportunity)}
      title={opportunity.title}
      showPublicationBadge={false}
      promotionBadge={candidatePromotionBadge(opportunity)}
      publishedAt={opportunity.published_at}
      saved={saved}
      isSaving={isSaving}
      onSaveToggle={canSave ? () => onSaveToggle(opportunity.id) : undefined}
      matchedSkillKeys={matchedSkillKeys}
      description={opportunity.description}
      meta={toOpportunityJobSummaryInput(opportunity)}
      organisationLabel={opportunity.organisation_name}
      organisationLogoUrl={resolveOpportunityOrganisationLogoUrl(
        opportunity,
        tenantLogoFallbackSrc,
      )}
      listingProvider={resolveListingProviderFromOpportunity(opportunity)}
      skills={mapOpportunitySkillsForCard(opportunity.skills)}
      trailing={
        <JobApplicationTrailing
          application={application}
          onApply={() => onApply(opportunity)}
        />
      }
      matchBand={fitProps?.matchBand}
      matchBandCaption={fitProps?.matchBandCaption}
      fitInsight={fitProps?.fitInsight}
    />
  )
})
